import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, PencilSquareIcon, FunnelIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') return Object.values(name).find(v => v) || '';
  return String(name);
};

function FilamentCard({ filament, actionLabel, actionClass, onAction, disabled }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded border border-slate-300 flex-shrink-0"
          style={{ backgroundColor: filament.color?.hex ? `#${filament.color.hex}` : '#f1f5f9' }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate leading-tight">{filament.fullName || filament.name}</p>
          <p className="text-xs text-slate-400 truncate leading-tight">
            {typeof filament.material === 'object'
              ? `${getDisplayName(filament.material.name)} (${filament.material.key})`
              : filament.material}
            {filament.materialExtra && (
              <span className="ml-1">
                · {typeof filament.materialExtra === 'object'
                  ? getDisplayName(filament.materialExtra.name) || filament.materialExtra.key
                  : filament.materialExtra}
              </span>
            )}
            {filament.color?.name && <span className="ml-1">· {filament.color.name}</span>}
          </p>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-slate-400">
          {[filament.diameter && `${filament.diameter}mm`, filament.weight && `${filament.weight}g`].filter(Boolean).join(' · ')}
        </span>
        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            disabled={disabled}
            className={`px-2 py-1 text-xs font-medium text-white rounded disabled:opacity-50 transition-colors ${actionClass}`}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminBarcodeModal({ barcode, barcodeResult, onEdit, onAssign, onCreateNew, onCancel }) {
  const resultType = barcodeResult?.type || 'not_found';
  const isNotFound = resultType === 'not_found';

  // Not-found search state
  const [filaments, setFilaments] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialExtras, setMaterialExtras] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({ search: '', material: '', materialExtra: '', brand: '' });
  const [loadingList, setLoadingList] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isNotFound) return;
    fetchFilterOptions();
    fetchFilaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotFound]);

  useEffect(() => {
    if (!isNotFound) return;
    fetchFilaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.material, filters.materialExtra, filters.brand]);

  const fetchFilterOptions = async () => {
    try {
      const [mRes, eRes, bRes] = await Promise.all([
        api.get('/auth/materials?ownership=system'),
        api.get('/auth/material-extras?ownership=system'),
        api.get('/auth/filament-brands?ownership=system'),
      ]);
      setMaterials(mRes.data.data || []);
      setMaterialExtras(eRes.data.data || []);
      setBrands(bRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const fetchFilaments = async () => {
    setLoadingList(true);
    try {
      const params = { ownership: 'system' };
      if (filters.material) params.material = filters.material;
      if (filters.materialExtra) params.extra = filters.materialExtra;
      if (filters.brand) params.brand = filters.brand;
      if (filters.search) params.q = filters.search;
      const res = await api.get('/auth/filaments', { params });
      setFilaments(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch system filaments:', err);
    } finally {
      setLoadingList(false);
    }
  };

  const clearFilters = () => setFilters({ search: '', material: '', materialExtra: '', brand: '' });
  const hasActiveFilters = filters.search || filters.material || filters.materialExtra || filters.brand;

  const handleAssignToFilament = async (filament) => {
    setLoading(true);
    setError('');
    try {
      const existingBarCodes = Array.isArray(filament.barCodes) ? filament.barCodes : [];
      await onAssign({ filament: filament._id, barCodes: [...existingBarCodes, barcode] });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign barcode');
      setLoading(false);
    }
  };

  const matchedFilament = barcodeResult?.data;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`bg-white rounded-xl shadow-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${isNotFound ? 'max-w-5xl' : 'max-w-2xl'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {isNotFound ? 'Barcode Not Found' : 'System Filament Found'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {isNotFound
                ? `No system filament found for barcode ${barcode}`
                : `Barcode ${barcode} matches a system filament`}
            </p>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Filters — only for not_found */}
        {isNotFound && (
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 mb-3">
              <FunnelIcon className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Select a system filament to assign this barcode to</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 ml-2">
                  Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="Search by name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchFilaments()}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={filters.material}
                onChange={(e) => setFilters({ ...filters, material: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">All Materials</option>
                {materials.map((m) => (
                  <option key={m._id} value={m._id}>{getDisplayName(m.name)} ({m.key})</option>
                ))}
              </select>
              <select
                value={filters.materialExtra}
                onChange={(e) => setFilters({ ...filters, materialExtra: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">All Extras</option>
                {materialExtras.map((me) => (
                  <option key={me._id} value={me._id}>{getDisplayName(me.name)} ({me.key})</option>
                ))}
              </select>
              <select
                value={filters.brand}
                onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">All Brands</option>
                {brands.map((b) => (
                  <option key={b._id} value={b._id}>{getDisplayName(b.name)}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {/* Match: single filament card */}
          {!isNotFound && matchedFilament && (
            <div className="max-w-sm mx-auto">
              <FilamentCard filament={matchedFilament} actionLabel={null} />
            </div>
          )}

          {/* Not found: system filament grid */}
          {isNotFound && (
            loadingList ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-slate-500">Loading system filaments...</div>
              </div>
            ) : filaments.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-slate-400 text-lg mb-2">No system filaments found</div>
                <p className="text-sm text-slate-500">
                  {hasActiveFilters ? 'Try adjusting your filters or create a new system filament.' : 'Create a new system filament to assign this barcode.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {filaments.map((f) => (
                  <FilamentCard
                    key={f._id}
                    filament={f}
                    actionLabel={loading ? 'Assigning...' : 'Assign barcode'}
                    actionClass="bg-blue-600 hover:bg-blue-700"
                    onAction={() => handleAssignToFilament(f)}
                    disabled={loading}
                  />
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-500">
            {isNotFound && !loadingList && `${filaments.length} system filament${filaments.length !== 1 ? 's' : ''} available`}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            {isNotFound ? (
              <button
                type="button"
                onClick={onCreateNew}
                disabled={loading}
                className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Create New System Filament
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onEdit(matchedFilament)}
                disabled={loading}
                className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
              >
                <PencilSquareIcon className="h-4 w-4 mr-1" />
                Edit Filament
              </button>
            )}
          </div>
        </div>

        {loading && isNotFound && (
          <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center rounded-xl">
            <div className="text-slate-600 font-medium">Assigning barcode...</div>
          </div>
        )}
      </div>
    </div>
  );
}
